
import { Email } from "../types";
import { ApiEmail, ApiEmailDetail, EmailListItem, EmailMessageDetailResponse, FolderEmailDetail, FolderEmailListItem } from "../api/emailApiTypes";

const statusForFolder = (folder: string): Email['status'] =>
  folder === 'sent' ? 'sent' : folder === 'drafts' ? 'draft' : 'received';

export const transformFolderEmailToEmail = (item: FolderEmailListItem, folder: string): Email => ({
  id: item.id,
  from: item.from_address || '',
  from_name: item.from_name || '',
  to: item.to_addresses || [],
  cc: item.cc_addresses || [],
  bcc: item.bcc_addresses || [],
  subject: item.subject || '(No subject)',
  body: item.snippet || '',
  body_html: '',
  date: item.date,
  received_date: item.date,
  read: item.is_read,
  // Backend may include is_starred on list items; map when present.
  starred: (item as { is_starred?: boolean }).is_starred ?? false,
  folder,
  status: statusForFolder(folder),
  attachments: [],
  has_attachments: item.has_attachments,
});

export const transformFolderEmailDetailToEmail = (item: FolderEmailDetail, folder?: string): Email => {
  const resolvedFolder = folder || item.folder;
  return {
    id: item.id,
    from: item.from_address || '',
    from_name: item.from_name || '',
    to: item.to || [],
    cc: item.cc || [],
    bcc: item.bcc || [],
    subject: item.subject || '(No subject)',
    body: item.body || '',
    body_html: item.body_html || '',
    date: item.date,
    received_date: item.date,
    read: item.is_read,
    starred: (item as { is_starred?: boolean }).is_starred ?? false,
    folder: resolvedFolder,
    thread_id: item.thread_id || '',
    reply_to: item.reply_to || '',
    status: statusForFolder(resolvedFolder),
    has_attachments: (item.attachments?.length || 0) > 0,
    attachments: (item.attachments || []).map((attachment) => ({
      id: attachment.id,
      email_id: item.id,
      name: attachment.name,
      size: attachment.size,
      content_type: attachment.content_type,
      content: '',
    })),
  };
};

/**
 * Transform API email list item to Email interface
 */
export const transformApiEmailToEmail = (apiEmail: ApiEmail, folder: string): Email => {
  const transformedEmail: Email = {
    id: apiEmail.id,
    from: apiEmail.from_address,
    from_name: apiEmail.from_name || '',
    to: Array.isArray(apiEmail.to) ? apiEmail.to : (apiEmail.to ? [apiEmail.to] : []),
    cc: [],
    bcc: [],
    subject: apiEmail.subject,
    body: apiEmail.snippet || '',
    body_html: '',
    date: apiEmail.date,
    received_date: apiEmail.date,
    read: apiEmail.is_read,
    folder: folder, // Always use the folder parameter passed in
    thread_id: '',
    reply_to: '',
    status: 'received' as const,
    attachments: []
  };
  
  // Force the folder to be what we expect, not what API returns
  transformedEmail.folder = folder;
  console.log(`📧 Service transformer: Email ${apiEmail.id} set to folder: ${folder}`);
  
  return transformedEmail;
};

/**
 * Transform detailed API email to Email interface
 */
export const transformApiEmailDetailToEmail = (response: ApiEmailDetail, folder?: string): Email => {
  const transformedEmail: Email = {
    id: response.id,
    from: response.from_address,
    from_name: response.from_name || '',
    to: Array.isArray(response.to) ? response.to : (response.to ? [response.to] : []),
    cc: response.cc || [],
    bcc: response.bcc || [],
    subject: response.subject || '(No subject)',
    body: response.body || '',
    body_html: response.body_html || '',
    date: response.date,
    received_date: response.date,
    read: response.is_read,
    folder: folder || response.folder || 'inbox', // Use provided folder first
    thread_id: response.thread_id || '',
    reply_to: response.reply_to || '',
    status: 'received' as const,
    attachments: response.attachments || []
  };
  
  // If folder was explicitly provided, force it
  if (folder) {
    transformedEmail.folder = folder;
    console.log(`📧 Service detail transformer: Email ${response.id} forced to folder: ${folder}`);
  }
  
  return transformedEmail;
};

/**
 * Transform new unified API list item to Email interface
 */
export const transformEmailListItemToEmail = (item: EmailListItem): Email => {
  const truncatedSnippet = item.snippet 
    ? (item.snippet.length > 80 ? item.snippet.substring(0, 80) + '...' : item.snippet)
    : 'No preview available';
  
  return {
    id: item.email_id,
    from: item.from_email || 'Loading...',
    from_name: item.from_name || 'Unknown Sender',
    to: [],
    cc: [],
    bcc: [],
    subject: item.subject,
    body: truncatedSnippet,
    body_html: '',
    date: item.received_at || item.sent_at,
    received_date: item.received_at,
    read: !item.is_unread, // Invert is_unread to get read status
    folder: item.folder,
    thread_id: item.thread_id || '',
    reply_to: '',
    status: item.folder === 'sent' ? 'sent' : 
            item.folder === 'drafts' ? 'draft' : 'received',
    attachments: item.has_attachments ? [] : []
  };
};

/**
 * Transform new unified API message detail to Email interface
 */
export const transformMessageDetailToEmail = (response: EmailMessageDetailResponse): Email => {
  const { message, participants, attachments } = response;
  
  return {
    id: message.email_id,
    from: participants.from.email_address,
    from_name: participants.from.display_name,
    to: participants.to.map(p => p.email_address),
    cc: participants.cc.map(p => p.email_address),
    bcc: [],
    subject: message.subject,
    body: message.body,
    body_html: message.body_html,
    date: message.received_at || message.sent_at,
    received_date: message.received_at,
    read: !message.is_unread, // Invert is_unread to get read status
    folder: message.folder,
    thread_id: message.thread_id || '',
    reply_to: '',
    status: message.folder === 'sent' ? 'sent' :
            message.folder === 'drafts' ? 'draft' : 'received',
    attachments: attachments.map(att => ({
      id: att.id,
      email_id: message.email_id,
      name: att.name,
      size: att.size,
      content_type: att.content_type,
      content: ''
    }))
  };
};

/**
 * Extract email ID from URL or path
 */
export const extractEmailId = (id: string): string => {
  const emailIdMatch = id.match(/([a-f0-9-]{36})$/);
  return emailIdMatch ? emailIdMatch[1] : id;
};
